// GES-385 — pure view-model for the order page's Fees tab. Reads the rows GET /api/sales-orders/:op?lines_only=1 already
// returns (`fees`, discount-stamped rows excluded server-side like the Add Fees panel) plus the order's customer credit
// notes (sale.totals.credit_notes.items). Display only: no money is computed here beyond summing the API's own amounts.
const TOL = 0.005;
const num = (v) => Number(v) || 0;
const r2 = (v) => Math.round(v * 100) / 100;

// ar_effect_reason is the server's own vocabulary (the lines_only branch of GET /api/sales-orders/:op).
const STAMP_KEY = {
  'billed to customer': 'billed',
  'settled at the till — no receivable': 'till',
  'absorbed — no customer charge': 'absorbed',
  'deducted from receivable': 'deducted',
  'reversed by cancellation': 'reversed',
};
// the lines_only read writes the reason with an em dash, the enriched /sales-fees read with a hyphen: one key for both
const reasonKey = (r) => String(r || '').replace(/\s*[\u2014\u2013-]\s*/g, ' — ');
export const feeStampKey = (f) => (f.stamped_treatment === 'pass_through' ? 'passThrough' : (STAMP_KEY[reasonKey(f.ar_effect_reason)] || null));

// charged = the customer carries it (bearer charge_customer); absorbed = the house carries it.
export const feeTreatment = (f) => (f.stamped_treatment === 'pass_through' ? 'passThrough' : f.bearer === 'charge_customer' ? 'charge' : 'absorb');

// 'cancelled' once any disposition leg exists (written_off | released_to_pool | refunded_to_bank, derived server-side).
export const feeStatus = (f, waivedIds) => {
  const tags = Array.isArray(f.tags) ? f.tags : [];
  return { waived: waivedIds.has(f.id), dispositions: tags, cancelled: tags.length > 0 };
};

// paid: only a fee billed to the customer can be paid. 'na' for absorbed / reversed.
export const feePaid = (f, treatment, cancelled) => {
  if (treatment === 'absorb' || cancelled || f.ar_effect_reason === 'reversed by cancellation') return 'na';
  if (reasonKey(f.ar_effect_reason) === 'settled at the till — no receivable') return 'yes';
  const paid = num(f.paid_amount), amt = num(f.amount);
  return paid >= amt - TOL && amt > 0 ? 'yes' : paid > TOL ? 'partial' : 'no';
};

export function buildFeesView(fees, creditNotes) {
  const waivedIds = new Set((creditNotes || []).filter(n => n.status === 'issued' && n.reason === 'waived_fee' && n.sales_fee_id).map(n => n.sales_fee_id));
  const rows = (fees || []).map(f => {
    const treatment = feeTreatment(f);
    const st = feeStatus(f, waivedIds);
    return {
      id: f.id, bucket: f.bucket || null, date: f.date || null, amount: num(f.amount), treatment,
      stamp: feeStampKey(f), ...st, paid: feePaid(f, treatment, st.cancelled), paidAmount: num(f.paid_amount),
      partner: f.partner_name || null, notes: f.notes || null,
      writtenOff: num(f.written_off), released: num(f.released_to_pool), refunded: num(f.refunded_to_bank),
    };
  });
  const live = rows.filter(r => !r.cancelled);
  const sum = (a) => r2(a.reduce((t, r) => t + r.amount, 0));
  return {
    rows, empty: rows.length === 0,
    totals: {
      all: sum(rows), live: sum(live),
      charged: sum(live.filter(r => r.treatment !== 'absorb')),
      absorbed: sum(live.filter(r => r.treatment === 'absorb')),
      cancelled: sum(rows.filter(r => r.cancelled)),
      paid: r2(live.filter(r => r.treatment !== 'absorb').reduce((t, r) => t + (r.paid === 'yes' && r.stamp === 'till' ? r.amount : r.paidAmount), 0)),
    },
  };
}
